import { useModal, Modal } from '@client/new/components/Modal/Modal';
import { useForm } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { WebhooksApi } from '../api/WebhooksApi';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import axios from 'axios';
import { addDays } from 'date-fns/esm';
import { PrimaryButton } from '@client/new/components/button';
import { Select, TextField } from '@client/new/components/basic';
import { InputLabel, MenuItem, Typography } from '@mui/material';
import { trackEvent } from '@services/tracking';

export enum Trigger {
  NewLead = 'New Lead',
  NewTrial = 'New Trial',
  NewSubscriber = 'New Subscription',
  NewOTP = 'New One-Time Purchase',
  NewFreeOffer = 'New Free Offer Redemption',
  SubscriberUpgraded = 'Subscription Upgrade',
  SubscriberCancelled = 'Subscription Cancel',
  SubscriberExpired = 'Subscription Expired',
  SubscriberResumed = 'Subscription Reactivated',
}

type NewWebhookModalProps = {
  influencerId: number;
  onClose: any;
};
const NewWebhookModal = ({ influencerId, onClose }: NewWebhookModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const createWebhook = useCallback((values: any) => {
    trackEvent({
      type: 'Tapped Add Webhook',
      payload: {
        'Trigger Type': values.trigger,
      },
    });

    WebhooksApi.createWebhook({
      influencerId,
      trigger: values.trigger,
      webhookUrl: values.webhookUrl,
    })
      .then(() => {
        toast.success('Webhook added');
        onClose();
      })
      .catch(() => toast.error('Could not add webhook'));
  }, []);

  return (
    <Modal title={'Add New Notification'} onClose={onClose} isOpen>
      <form
        onSubmit={handleSubmit(createWebhook)}
        className="flex flex-col gap-5"
      >
        <div className="flex flex-col gap-2">
          <label>Trigger</label>
          <Select
            {...register('trigger', { required: 'This field is required' })}
          >
            {Object.entries(Trigger).map(([key, value]) => (
              <MenuItem value={key}>{value}</MenuItem>
            ))}
          </Select>
          {errors.trigger && (
            <p className="text-red-500">{errors.trigger.message?.toString()}</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label>Webhook url</label>
          <TextField
            placeholder="https://hooks.zapier.com/hooks/catch/1234567/abcdefgh/"
            {...register('webhookUrl', {
              required:
                'This field is required and needs to be a valid Zapier webhook url.',
            })}
          />
          {errors.webhookUrl && (
            <p className="text-red-500">
              {errors.webhookUrl.message?.toString()}
            </p>
          )}
        </div>
        <PrimaryButton type="submit" className="mt-10">
          Add webhook
        </PrimaryButton>
      </form>
    </Modal>
  );
};

type DeleteButtonProps = {
  influencerId: number;
  webhookId: string;
  onDelete: () => void;
};
const DeleteButton = ({
  influencerId,
  webhookId,
  onDelete,
}: DeleteButtonProps) => {
  const [loading, setLoading] = useState(false);
  return (
    <div
      onClick={() => {
        if (loading) return;
        setLoading(true);
        WebhooksApi.deleteWebhook({
          influencerId,
          webhookId,
        })
          .then(() => {
            onDelete();
            return toast.success('Deleted');
          })
          .catch(() => toast.error('Could not delete'))
          .finally(() => setLoading(false));
      }}
      className="text-red-600 font-bold px-3 py-2 rounded-xl cursor-pointer"
    >
      Delete {loading ? '...' : ''}
    </div>
  );
};

/**
 * @param {keyof Trigger} trigger
 * @param {string} email
 * @return {{expiryDate: string, discountCode: null, allowMarketing: boolean, billingCycle: string, name: string, trigger: "toString" | "charAt" | "charCodeAt" | "concat" | "indexOf" | "lastIndexOf" | "localeCompare" | "match" | "replace" | "search" | "slice" | "split" | "substring" | "toLowerCase" | "toLocaleLowerCase" | "toUpperCase" | "toLocaleUpperCase" | "trim" | "length" | "substr" | "valueOf" | "at" | "codePointAt" | "includes" | "endsWith" | "normalize" | "repeat" | "startsWith" | "anchor" | "big" | "blink" | "bold" | "fixed" | "fontcolor" | "fontsize" | "italics" | "link" | "small" | "strike" | "sub" | "sup" | "[Symbol.iterator]" | "padStart" | "padEnd" | "trimEnd" | "trimStart" | "trimLeft" | "trimRight" | "matchAll" | "replaceAll", plan: string, email: string, platform: string, promotion: null} | {allowMarketing: boolean, name: string, trigger: "toString" | "charAt" | "charCodeAt" | "concat" | "indexOf" | "lastIndexOf" | "localeCompare" | "match" | "replace" | "search" | "slice" | "split" | "substring" | "toLowerCase" | "toLocaleLowerCase" | "toUpperCase" | "toLocaleUpperCase" | "trim" | "length" | "substr" | "valueOf" | "at" | "codePointAt" | "includes" | "endsWith" | "normalize" | "repeat" | "startsWith" | "anchor" | "big" | "blink" | "bold" | "fixed" | "fontcolor" | "fontsize" | "italics" | "link" | "small" | "strike" | "sub" | "sup" | "[Symbol.iterator]" | "padStart" | "padEnd" | "trimEnd" | "trimStart" | "trimLeft" | "trimRight" | "matchAll" | "replaceAll", source: string, email: string, url: string} | {allowMarketing: boolean, name: string, trigger: "toString" | "charAt" | "charCodeAt" | "concat" | "indexOf" | "lastIndexOf" | "localeCompare" | "match" | "replace" | "search" | "slice" | "split" | "substring" | "toLowerCase" | "toLocaleLowerCase" | "toUpperCase" | "toLocaleUpperCase" | "trim" | "length" | "substr" | "valueOf" | "at" | "codePointAt" | "includes" | "endsWith" | "normalize" | "repeat" | "startsWith" | "anchor" | "big" | "blink" | "bold" | "fixed" | "fontcolor" | "fontsize" | "italics" | "link" | "small" | "strike" | "sub" | "sup" | "[Symbol.iterator]" | "padStart" | "padEnd" | "trimEnd" | "trimStart" | "trimLeft" | "trimRight" | "matchAll" | "replaceAll", program: string, email: string} | {}}
 */
const getExamplePayload = (trigger: keyof Trigger, email: string) => {
  const basicInfo = {
    trigger,
    name: 'Jacques Bonhomme',
    email,
    allowMarketing: true,
  };

  switch (Trigger[trigger]) {
    case Trigger.NewLead:
      return {
        ...basicInfo,
        source: 'instagram (example)',
        url: 'https://playbook.test-app.link/example',
      };
    case Trigger.NewTrial:
    case Trigger.NewSubscriber:
    case Trigger.SubscriberUpgraded:
    case Trigger.SubscriberCancelled:
    case Trigger.SubscriberResumed:
    case Trigger.SubscriberExpired:
      return {
        ...basicInfo,
        plan: 'Premium',
        billingCycle: 'Monthly',
        platform: 'stripe',
        expiryDate: addDays(new Date(), 30).toISOString(),
        promotion: null,
        discountCode: null,
      };
    case Trigger.NewOTP:
      return {
        ...basicInfo,
        program: 'Example One-Time Purchase Name',
      };
    case Trigger.NewFreeOffer:
      return {
        ...basicInfo,
        offerName: 'Example Free Offer Name',
      };
    default:
      return {};
  }
};

export const WebhookTest = ({ influencerId }: { influencerId: number }) => {
  const { register, handleSubmit } = useForm();

  return (
    <form
      onSubmit={handleSubmit(async (values: any) => {
        const webhooks = await WebhooksApi.getWebhooks({ influencerId });
        const grouped = _.groupBy(webhooks ?? [], (w) => w.trigger);
        const urls = grouped[values.trigger];

        if (!urls?.length) {
          return toast.error('Notifications have not been configured');
        }

        await Promise.all(
          urls.map(async (webhook) => {
            await (webhook.webhookUrl.startsWith('https://hooks.zapier.com')
              ? axios.create({
                  transformRequest: [(data, _headers) => JSON.stringify(data)],
                })
              : axios
            )
              .post(
                webhook.webhookUrl,
                getExamplePayload(values.trigger, values.email),
                {
                  withCredentials: false,
                },
              )
              .then(() =>
                toast.success(
                  `Sent test ${values.trigger} notification to ${webhook.webhookUrl}`,
                ),
              )
              .catch(() =>
                toast.error(
                  `Could not send a test notification ${webhook.webhookUrl}`,
                ),
              );
          }),
        );
      })}
      className="flex flex-col items-start"
    >
      <Typography variant="h5" className="mb-5">
        Test notification
      </Typography>

      <div className="flex flex-col items-start gap-4">
        <div>
          <InputLabel className="mb-2">Trigger</InputLabel>
          <Select
            {...register('trigger', { required: true })}
            className="w-[240px]"
          >
            {Object.entries(Trigger).map(([key, value]) => (
              <MenuItem value={key}>{value}</MenuItem>
            ))}
          </Select>
        </div>
        <div>
          <InputLabel className="mb-2">Email</InputLabel>

          <TextField
            InputProps={{
              placeholder: 'john@example.com',
              style: {
                width: '240px',
              },
            }}
            {...register('email', { required: true })}
          />
        </div>
        <PrimaryButton type="submit">Test</PrimaryButton>
      </div>
    </form>
  );
};

export const WebhookSettings = ({ influencerId }: { influencerId: number }) => {
  const { data, refetch } = useQuery(
    ['webhooks', influencerId],
    () => WebhooksApi.getWebhooks({ influencerId }),
    { enabled: !!influencerId, refetchOnWindowFocus: false },
  );

  const modal = useModal(({ onClose }: any) => {
    const onModalClose = () => {
      refetch();
      onClose();
    };

    return (
      <NewWebhookModal onClose={onModalClose} influencerId={influencerId} />
    );
  });

  return (
    <div>
      <modal.Popup />
      <PrimaryButton
        onClick={() => {
          modal.open();

          trackEvent({
            type: 'Tapped Add New Notification',
            payload: {},
          });
        }}
      >
        Add New Notification
      </PrimaryButton>
      <div className="flex flex-col gap-2 mt-5">
        {data?.map((w) => (
          <div className="flex items-center justify-between gap-4 border border-gray-200 p-3 rounded-xl">
            <div className="flex items-center gap-4">
              <div className="rounded-xl flex items-center gap-2">
                On
                <span className="rounded-full px-3 py-2 bg-yellow-500 font-bold text-white">
                  {Trigger[w.trigger]}
                </span>
              </div>
              <div className="rounded-xl flex items-center gap-2">
                Trigger{' '}
                <a
                  href={w.webhookUrl}
                  className="rounded-full text-black px-3 py-2 border border-primary bg-primary-200"
                  target="_blank"
                >
                  {w.webhookUrl}
                </a>
              </div>
            </div>
            <DeleteButton
              influencerId={influencerId}
              webhookId={w.id}
              onDelete={refetch}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
